import { Api } from '@/api/api.client';
import { type Components } from '@/api/generated/api';
import { AsyncOperationStatus } from '@/api/generated/api.enum';
import { upsertActionToast, type IActionToastBehavior, type IToastAction } from '@/composables/useToast';
import { toast, type Id } from 'vue3-toastify';
import { useI18n } from 'vue-i18n';
import { now } from 'common/utils/date.utils';
import { delay } from 'common/utils/function.utils';
import { isTypeOfString } from 'common/utils/string.utils';

const DEFAULT_OPERATION_POLL_INTERVAL_MS = 1000;

interface IAsyncOperationToastMessages {
  pending: string;
  success: string;
  error: string;
}

interface IStartAsyncOperationCompletedParams {
  operationUuid: string;
  startResult: Components.Schemas.AsyncOperationCreatedResponseDto;
  status: Components.Schemas.AsyncOperationStatusDto;
}

interface IStartAsyncOperationFailedParams {
  error: Error;
  operationUuid: string;
  startResult: Components.Schemas.AsyncOperationCreatedResponseDto | null;
  status: Components.Schemas.AsyncOperationStatusDto | null;
}

interface IAsyncOperationToastActionParams<TResult> {
  operationUuid: string;
  startResult: Components.Schemas.AsyncOperationCreatedResponseDto;
  status: Components.Schemas.AsyncOperationStatusDto;
  result: TResult | undefined;
}

interface IAsyncOperationToastAction<TResult> {
  label: string;
  onClick: (params: IAsyncOperationToastActionParams<TResult>) => Promise<void> | void;
}

export interface IStartAsyncOperationParams<TResult = void> {
  start: () => Promise<Components.Schemas.AsyncOperationCreatedResponseDto>;
  onCompleted?: (params: IStartAsyncOperationCompletedParams) => Promise<TResult> | TResult;
  onFailed?: (params: IStartAsyncOperationFailedParams) => Promise<void> | void;
  pollIntervalMs?: number;
  hideToast?: boolean;
  shouldBlockFrontend?: boolean;
  successAction?: IAsyncOperationToastAction<TResult>;
  toastMessages?: Partial<IAsyncOperationToastMessages>;
}

export interface IStartedAsyncOperation<TResult = void> {
  finished: Promise<TResult | undefined>;
  operationUuid: string;
}

export interface IRunAsyncOperationParams<TResult> {
  run: () => Promise<TResult>;
  toastMessages?: Partial<IAsyncOperationToastMessages>;
  /** Replaces toastMessages.error with a message derived from the failure, e.g. its backend error type. */
  getErrorMessage?: (error: Error) => string;
}

interface IUseAsyncOperations {
  startAsyncOperation: <TResult = void>(
    params: IStartAsyncOperationParams<TResult>,
  ) => Promise<IStartedAsyncOperation<TResult>>;
  runAsyncOperation: <TResult>(params: IRunAsyncOperationParams<TResult>) => Promise<TResult>;
}

const OPERATION_TOAST_BEHAVIOR: IActionToastBehavior = {
  autoClose: false,
  closeButton: false,
  closeOnClick: false,
  hideProgressBar: true,
  pauseOnFocusLoss: false,
  pauseOnHover: false,
  bodyClassName: 'toast-body--rich',
};

const createToastId = (): string => `async-operation:${now().getTime()}:${Math.random().toString(36).slice(2)}`;

const normalizeError = (error: unknown, fallbackMessage: string): Error => {
  if (error instanceof Error) return error;
  if (isTypeOfString(error) && error.trim()) return new Error(error);
  return new Error(fallbackMessage);
};

const showLoadingToast = (toastId: Id, message: string): void => {
  if (toast.isActive(toastId)) {
    toast.update(toastId, {
      ...OPERATION_TOAST_BEHAVIOR,
      isLoading: true,
      render: message,
      type: 'loading',
    });
    return;
  }

  toast.loading(message, {
    ...OPERATION_TOAST_BEHAVIOR,
    toastId,
  });
};

const updateToast = (
  toastId: Id,
  params: {
    message: string;
    type: 'success' | 'error';
    action?: IToastAction;
  },
): void => {
  upsertActionToast(toastId, { ...params, behavior: OPERATION_TOAST_BEHAVIOR });
};

const getOperationUuid = (startResult: Components.Schemas.AsyncOperationCreatedResponseDto): string => {
  const operationUuid = startResult.data.uuid;
  if (operationUuid === '') {
    throw new Error('No operation id returned');
  }

  return operationUuid;
};

const getOperationStatus = async (operationUuid: string): Promise<Components.Schemas.AsyncOperationStatusDto> => {
  const response = await Api.Routes.AsyncOperations.getStatusByUuid({ uuid: operationUuid });
  return response.data;
};

export const useAsyncOperations = (): IUseAsyncOperations => {
  const { t } = useI18n();

  const pollUntilFinished = async (params: {
    operationUuid: string;
    pollIntervalMs: number;
  }): Promise<Components.Schemas.AsyncOperationStatusDto> => {
    const pollOnce = async (): Promise<Components.Schemas.AsyncOperationStatusDto> => {
      const status = await getOperationStatus(params.operationUuid);

      if (status.status === AsyncOperationStatus.failed || status.status === AsyncOperationStatus.cancelled) {
        throw new Error(status.error ?? 'Operation failed');
      }

      if (status.status === AsyncOperationStatus.completed) {
        return status;
      }

      await delay(params.pollIntervalMs);
      return pollOnce();
    };

    return pollOnce();
  };

  const defaultToastMessages = (): IAsyncOperationToastMessages => ({
    pending: t('asyncOperations.pending'),
    success: t('asyncOperations.success'),
    error: t('asyncOperations.error'),
  });

  const startAsyncOperation = async <TResult = void>(
    params: IStartAsyncOperationParams<TResult>,
  ): Promise<IStartedAsyncOperation<TResult>> => {
    const toastMessages = { ...defaultToastMessages(), ...params.toastMessages };
    const hideToast = params.hideToast ?? false;
    const toastId = hideToast ? null : createToastId();
    const pollIntervalMs = params.pollIntervalMs ?? DEFAULT_OPERATION_POLL_INTERVAL_MS;
    const shouldBlockFrontend = params.shouldBlockFrontend ?? false;
    let startedOperation: {
      operationUuid: string;
      startResult: Components.Schemas.AsyncOperationCreatedResponseDto;
    };

    if (toastId !== null) {
      showLoadingToast(toastId, toastMessages.pending);
    }

    try {
      const startResult = await params.start();
      const operationUuid = getOperationUuid(startResult);

      startedOperation = {
        operationUuid,
        startResult,
      };
    } catch (error) {
      const normalizedError = normalizeError(error, toastMessages.error);
      if (toastId !== null) {
        updateToast(toastId, { message: toastMessages.error, type: 'error' });
      }
      throw normalizedError;
    }

    const activeOperation = startedOperation;

    const finished = pollUntilFinished({
      operationUuid: activeOperation.operationUuid,
      pollIntervalMs,
    })
      .then(async (status) => {
        const result = await params.onCompleted?.({
          operationUuid: activeOperation.operationUuid,
          startResult: activeOperation.startResult,
          status,
        });
        if (toastId !== null) {
          const successAction = params.successAction;
          updateToast(toastId, {
            action: successAction
              ? {
                  label: successAction.label,
                  onClick: () => {
                    return successAction.onClick({
                      operationUuid: activeOperation.operationUuid,
                      result,
                      startResult: activeOperation.startResult,
                      status,
                    });
                  },
                }
              : undefined,
            message: toastMessages.success,
            type: 'success',
          });
        }
        return result;
      })
      .catch(async (error: unknown) => {
        const normalizedError = normalizeError(error, toastMessages.error);
        try {
          await params.onFailed?.({
            error: normalizedError,
            operationUuid: activeOperation.operationUuid,
            startResult: activeOperation.startResult,
            status: null,
          });
        } catch (onFailedError) {
          console.error('Async operation failure callback failed', onFailedError);
        } finally {
          if (toastId !== null) {
            updateToast(toastId, { message: toastMessages.error, type: 'error' });
          }
        }
        throw normalizedError;
      });

    void finished.catch(() => undefined);

    if (shouldBlockFrontend) {
      await finished;
    }

    return {
      finished,
      operationUuid: activeOperation.operationUuid,
    };
  };

  // The same toast for work that finishes within its request, so there is no operation uuid to poll.
  const runAsyncOperation = async <TResult>(params: IRunAsyncOperationParams<TResult>): Promise<TResult> => {
    const toastMessages = { ...defaultToastMessages(), ...params.toastMessages };
    const toastId = createToastId();
    showLoadingToast(toastId, toastMessages.pending);
    try {
      const result = await params.run();
      updateToast(toastId, { message: toastMessages.success, type: 'success' });
      return result;
    } catch (cause) {
      const error = normalizeError(cause, toastMessages.error);
      updateToast(toastId, { message: params.getErrorMessage?.(error) ?? toastMessages.error, type: 'error' });
      throw error;
    }
  };

  return {
    startAsyncOperation,
    runAsyncOperation,
  };
};
